import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useUserDataV2 } from '@obeta/data/lib/hooks/useUserDataV2'
import {
  createDigitalBillingEntry,
  CreateDigitalBillingEntryResultAction,
  DigitalBillingActionTypes,
  getDigitalBillingEntry,
  GetDigitalBillingEntryResultAction,
} from '@obeta/data/lib/actions/digital-billing-actions'
import { useActionNotification } from '@obeta/data/lib/hooks/useActionNotification'
import { DigitalBillingMailInput } from '@obeta/schema'
import { MailType } from '@obeta/models/lib/models/SendMail/SendMail'
import { DocumentNode, gql, useApolloClient } from '@apollo/client'

export type BillingType = 'pdf' | 'zugferd'

type UseDigitalBillingActions = () => {
  isRegistered: boolean
  register: (billingType: string, email: string) => void
}

export const useDigitalBillingActions: UseDigitalBillingActions = () => {
  const [isRegistered, setIsRegistered] = useState<boolean>(false)
  const dispatch = useDispatch()
  const user = useUserDataV2()
  const apolloClient = useApolloClient()

  const waitForCreateDigitalBillingEntry = useActionNotification(
    DigitalBillingActionTypes.CreateDigitalBillingEntryResult
  )
  const waitForGetDigitalBillingEntry = useActionNotification(
    DigitalBillingActionTypes.GetDigitalBillingEntryResult
  )

  const isReadyForDigitalBillingActions = user.fullUserId !== undefined && user.allTokensExist

  const register = useCallback(
    async (billingType: string, email: string) => {
      if (isReadyForDigitalBillingActions) {
        dispatch(createDigitalBillingEntry(billingType, email))
        // Mail Input
        const input: DigitalBillingMailInput = {
          data: {
            billingType: billingType,
            email: email,
            userId: user.fullUserId ?? '',
          },
          type: MailType.DigitalBilling,
        }
        const sendDigitalBillingMail: DocumentNode = gql`
          query sendDigitalBillingMail($input: DigitalBillingMailInput!) {
            sendDigitalBillingMail(input: $input) {
              success
              errorCode
              errorMessage
            }
          }
        `
        // Send Mail
        const response = await apolloClient.query({
          query: sendDigitalBillingMail,
          variables: {
            input,
          },
        })
        if (!response.data.sendDigitalBillingMail.success) {
          console.error('Error sending email:', response.data.sendDigitalBillingMail.errorMessage)
        }
      }
    },
    [dispatch, isReadyForDigitalBillingActions, user.fullUserId, apolloClient]
  )

  useEffect(() => {
    if (isReadyForDigitalBillingActions) {
      dispatch(getDigitalBillingEntry())
    }
  }, [dispatch, isReadyForDigitalBillingActions])

  waitForCreateDigitalBillingEntry((response: CreateDigitalBillingEntryResultAction) => {
    setIsRegistered(response.success)
  })
  waitForGetDigitalBillingEntry((response: GetDigitalBillingEntryResultAction) => {
    setIsRegistered(response.alreadyRegistered)
  })

  return {
    isRegistered,
    register,
  }
}
